import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Search, Pencil, Trash2, ClipboardList } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { API } from "@/lib/api";


export default function TechnicalSheets() {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState(null);

  useEffect(() => {
    fetchSheets();
  }, []);

  const fetchSheets = async () => {
    try {
      const res = await axios.get(`${API}/technical-sheets`);
      setSheets(res.data);
    } catch (error) {
      toast.error("Erro ao carregar fichas técnicas");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSheet) return;

    try {
      await axios.delete(`${API}/technical-sheets/${selectedSheet.id}`);
      toast.success("Ficha técnica excluída com sucesso");
      setDeleteDialogOpen(false);
      setSelectedSheet(null);
      fetchSheets();
    } catch (error) {
      toast.error("Erro ao excluir ficha técnica");
    }
  };

  const filteredSheets = sheets.filter((sheet) =>
    sheet.name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="technical-sheets-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Fichas Técnicas
          </h1>
          <p className="text-slate-500 mt-1">Defina os insumos utilizados em cada procedimento</p>
        </div>
        <Link to="/technical-sheets/new">
          <Button className="bg-teal-600 hover:bg-teal-700" data-testid="add-technical-sheet-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nova Ficha Técnica
          </Button>
        </Link>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar ficha técnica..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-technical-sheets"
            />
          </div>
        </CardContent>
      </Card>

      {/* Technical Sheets Table */}
      <Card>
        <CardContent className="p-0">
          {filteredSheets.length === 0 ? (
            <div className="empty-state py-12">
              <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">
                {search ? "Nenhuma ficha técnica encontrada" : "Nenhuma ficha técnica cadastrada"}
              </p>
              {!search && (
                <Link to="/technical-sheets/new">
                  <Button className="mt-4 bg-teal-600 hover:bg-teal-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Criar primeira ficha técnica
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="min-w-[200px]">Nome do Procedimento</TableHead>
                    <TableHead className="w-28 text-center">Qtd. Itens</TableHead>
                    <TableHead className="w-36 text-right">Custo Total</TableHead>
                    <TableHead className="w-24 text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSheets.map((sheet) => (
                    <TableRow key={sheet.id} data-testid={`technical-sheet-row-${sheet.id}`}>
                      <TableCell>
                        <Link 
                          to={`/technical-sheets/${sheet.id}`}
                          className="font-medium text-slate-900 hover:text-teal-600"
                        >
                          {sheet.name}
                        </Link>
                        {sheet.description && (
                          <p className="text-sm text-slate-500 truncate max-w-[250px]">{sheet.description}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center justify-center min-w-[32px] px-2 py-1 rounded-md bg-slate-100 text-slate-700 text-sm font-mono">
                          {sheet.items?.length || 0}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono font-medium text-teal-700 whitespace-nowrap">
                          {formatCurrency(sheet.total_cost)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Link to={`/technical-sheets/${sheet.id}`}>
                            <Button variant="ghost" size="icon" data-testid={`edit-technical-sheet-${sheet.id}`}>
                              <Pencil className="w-4 h-4 text-slate-500" />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedSheet(sheet);
                              setDeleteDialogOpen(true);
                            }}
                            data-testid={`delete-technical-sheet-${sheet.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-rose-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Tem certeza que deseja excluir a ficha técnica <strong>{selectedSheet?.name}</strong>?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
